/**
 * Stop the widget rebuilding every ad on the page each time the browser fires
 * `resize`.
 *
 * WHY. `widget.sellwild.com/sellwild/sellwild-sellwild-next.js` registers, from
 * the `connectedCallback` of every `<sellwild-ad>`, a window `resize` listener
 * that re-runs the element's whole render after a 1s debounce:
 *
 *     function execute (element) {
 *       const theme = getCustomizations(window.location, element)
 *       const resize = debounce(() => { onAdd(element, theme, widgetId) }, 1000)
 *       if (!theme.noResize) window.addEventListener('resize', resize)
 *       return onAdd(element, theme, widgetId)
 *     }
 *
 * `onAdd` rebuilds the tag's markup and calls `googletag.defineSlot` again, so a
 * single resize event defines a SECOND slot for every ad already on the page,
 * re-requests it, and repaints it. Nothing destroys the old slot — the widget
 * has no `destroySlots` call anywhere in the bundle — so each pass also leaks
 * its slots into `swpbjs.gamSlots` for the life of the page view, and each pass
 * walks straight past `useAdSlot`, which counts React mounts rather than widget
 * re-renders inside an element that never unmounted.
 *
 * Measured on prod, iPhone 13 UA, home feed: 4 slots at rest; ONE synthetic
 * resize event took it to 8; four events took it to 20.
 *
 * WHY IT IS MOBILE-ONLY. A desktop browser fires `resize` when the reader
 * resizes the window, which is almost never. A phone fires it while SCROLLING —
 * the URL bar collapsing and expanding changes the viewport height. That is the
 * whole of the "ads flash on scroll, only on mobile, can't reproduce it in
 * device mode" report: a desktop browser's device mode has no URL bar to
 * collapse, and neither does a headless viewport, so neither fires the event.
 *
 * HOW. `window.addEventListener` is wrapped once, before any `<sellwild-ad>`
 * mounts, and a `resize` registration whose stack comes from the widget bundle
 * is dropped. Nothing else is touched: other `resize` listeners register
 * normally, and a registration we cannot attribute is left alone rather than
 * guessed at.
 *
 * WHY NOT THE SUPPORTED SWITCH. The widget skips the listener when
 * `theme.noResize` is set, and `getCustomizations` does read per-element
 * attributes. It does not work here: probed against the live bundle with
 * `no-resize`, `noResize`, `no_resize` and `NO_RESIZE` all set on the tag, all
 * four listeners still registered. The deployed build's customization map does
 * not carry that key, so there is nothing to set from our side.
 *
 * WHY DROPPING IT COSTS NOTHING. The listener exists so a responsive widget can
 * re-lay-out. `<sellwild-ad>` is the only widget element this app renders, and
 * we give it an explicit fixed `width`/`height` — a 300x250 is a 300x250 at
 * every viewport. There is no re-layout to do.
 *
 * REMOVE THIS once the widget stops rebuilding on a resize that did not change
 * the ad's own box. `window.__SW_DISABLE_AD_RESIZE_GUARD__ = true` before load
 * turns it off without a deploy.
 */

const WIDGET_HOST = 'widget.sellwild.com';

let installed = false;
let blocked = 0;

/**
 * Whether the code registering this listener came from the widget bundle.
 *
 * Fails OPEN. A browser that gives us no usable stack registers the listener as
 * it would have without this file — a page whose ads repaint is worse than one
 * whose ads repaint, but a page with an unrelated listener silently missing is
 * worse than both.
 */
function calledFromWidget(): boolean {
  let stack = '';
  try {
    throw new Error('sw-stack');
    // eslint-disable-next-line sellwild-code/catch-must-log -- the throw IS the stack probe; there is no failure here to report
  } catch (err) {
    stack = (err as Error)?.stack ?? '';
  }
  return stack.includes(WIDGET_HOST);
}

/**
 * Install the guard. Idempotent, and must run before the first `<sellwild-ad>`
 * is inserted — the widget registers its listener from that insertion. The ad
 * insert batcher holds every tag for at least `AD_INSERT_SETTLE_MS` after mount,
 * so an `_app` effect is comfortably early.
 */
export function installAdResizeRebuildGuard(): void {
  if (typeof window === 'undefined' || installed) return;

  const w = window as unknown as { __SW_DISABLE_AD_RESIZE_GUARD__?: boolean };
  if (w.__SW_DISABLE_AD_RESIZE_GUARD__) return;
  installed = true;

  const original = window.addEventListener.bind(window);

  window.addEventListener = function patchedAddEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject | null,
    options?: boolean | AddEventListenerOptions
  ): void {
    if (type === 'resize' && calledFromWidget()) {
      blocked += 1;
      return;
    }
    return original(type, listener as EventListener, options);
  } as typeof window.addEventListener;
}

/** Widget resize listeners dropped so far — for tests and diagnostics. */
export function blockedAdResizeListenerCount(): number {
  return blocked;
}

/** Tests only. The guard is installed once per document in the app. */
export function resetAdResizeRebuildGuard(): void {
  installed = false;
  blocked = 0;
}
